import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle, ShieldCheck } from 'lucide-react'
import { authApi, setAccessToken } from '@/lib/api'
import { ROUTES, APP } from '@/lib/constants'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { OAuthButtons, OAuthDivider } from '@/components/ui/oauth-buttons'
import { LegalAcceptanceCheckbox } from '@/components/ui/legal-acceptance'
import { markNeedsOrgSelection } from '@/lib/org-selection'

interface LoginNavState {
  mfaChallenge?: {
    token: string
    expiresIn: number
    email: string
  }
}

/**
 * Login page — real auth against tax-account.
 *
 * Two-step flow when MFA is enabled:
 *   1. email + password → if mfa_required, server returns a challenge token (no JWTs)
 *   2. user enters TOTP / backup code → trade challenge for the actual JWT pair
 */
type Stage =
  | { kind: 'credentials' }
  | { kind: 'mfa'; challengeToken: string; expiresIn: number; emailMasked: string }

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Hand-off from /oauth/callback when the user has MFA enrolled —
  // we land straight on the TOTP step instead of asking the password
  // we don't have (and don't need, since the provider already vouched).
  useEffect(() => {
    const state = location.state as LoginNavState | null
    if (state?.mfaChallenge) {
      setStage({
        kind: 'mfa',
        challengeToken: state.mfaChallenge.token,
        expiresIn: state.mfaChallenge.expiresIn,
        emailMasked: maskEmail(state.mfaChallenge.email),
      })
      // Wipe state so a refresh doesn't re-enter the MFA stage with
      // a now-consumed challenge.
      window.history.replaceState({}, '')
    }
  }, [location.state])

  const handleCredentials = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const resp = await authApi.login({ email: email.trim(), password })
      if (resp.mfa_required && resp.mfa_challenge_token) {
        // Don't store anything — the challenge token is held in component state
        // and consumed once on /auth/login/totp.
        setStage({
          kind: 'mfa',
          challengeToken: resp.mfa_challenge_token,
          expiresIn: resp.mfa_challenge_expires_in ?? 300,
          emailMasked: maskEmail(resp.email),
        })
      } else {
        setAccessToken(resp.access_token)
        await routePostLogin(navigate)
      }
    } catch (err) {
      setError(extractError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleTotp = async (e: FormEvent, code: string) => {
    e.preventDefault()
    if (stage.kind !== 'mfa') return
    setError(null)
    setLoading(true)
    try {
      const resp = await authApi.loginTotp({
        mfa_challenge_token: stage.challengeToken,
        code: code.trim(),
      })
      if (!resp.access_token) {
        setError('Respuesta inválida del servidor.')
        return
      }
      setAccessToken(resp.access_token)
      await routePostLogin(navigate)
    } catch (err) {
      setError(extractError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-app px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <a
            href={import.meta.env.VITE_APP_URL ?? 'https://taxor.co'}
            className="flex items-center gap-2"
          >
            <div
              aria-hidden
              className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-brand-500 to-brand-600 font-mono text-sm font-bold text-white shadow-sm"
            >
              Tx
            </div>
            <span className="text-base font-semibold text-fg-primary">{APP.name}</span>
          </a>
          <ThemeToggle />
        </div>

        <div className="rounded-xl border border-border bg-bg-surface p-8 shadow-sm">
          {stage.kind === 'credentials' ? (
            <CredentialsForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              accepted={accepted}
              setAccepted={setAccepted}
              error={error}
              loading={loading}
              onSubmit={handleCredentials}
            />
          ) : (
            <TotpForm
              emailMasked={stage.emailMasked}
              error={error}
              loading={loading}
              onSubmit={handleTotp}
              onBack={() => {
                setStage({ kind: 'credentials' })
                setError(null)
                setPassword('')
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Step 1: email + password ──────────────────────────────────────

function CredentialsForm({
  email,
  setEmail,
  password,
  setPassword,
  accepted,
  setAccepted,
  error,
  loading,
  onSubmit,
}: {
  email: string
  setEmail: (v: string) => void
  password: string
  setPassword: (v: string) => void
  accepted: boolean
  setAccepted: (v: boolean) => void
  error: string | null
  loading: boolean
  onSubmit: (e: FormEvent) => void
}) {
  return (
    <>
      <h1 className="text-2xl font-bold text-fg-primary">Iniciar sesión</h1>
      <p className="mt-2 text-sm text-fg-secondary">
        Accede al panel con el correo y la contraseña que registraste.
      </p>

      {error && (
        <div className="mt-5 flex items-start gap-2 rounded-lg bg-error-tint px-3 py-2.5 text-sm text-error-fg">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <LegalAcceptanceCheckbox
        accepted={accepted}
        setAccepted={setAccepted}
        actionVerb="iniciar sesión"
      />

      <div className="mt-6">
        <OAuthButtons intent="login" disabled={loading || !accepted} />
      </div>

      <OAuthDivider label="o con tu correo" />

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-xs font-medium uppercase tracking-wider text-fg-muted"
          >
            Correo electrónico
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            placeholder="tu@empresa.co"
          />
        </div>
        <div>
          <label
            htmlFor="password"
            className="block text-xs font-medium uppercase tracking-wider text-fg-muted"
          >
            Contraseña
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            placeholder="••••••••"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !accepted}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Verificando…
            </>
          ) : (
            <>
              Iniciar sesión
              <span aria-hidden>→</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-6 border-t border-border pt-6 text-sm text-fg-secondary">
        ¿Aún no tienes cuenta?{' '}
        <Link to={ROUTES.REGISTER} className="font-medium text-brand-600 hover:underline">
          Crear cuenta →
        </Link>
      </div>
    </>
  )
}

// ─── Step 2: TOTP ─────────────────────────────────────────────────

function TotpForm({
  emailMasked,
  error,
  loading,
  onSubmit,
  onBack,
}: {
  emailMasked: string
  error: string | null
  loading: boolean
  onSubmit: (e: FormEvent, code: string) => void
  onBack: () => void
}) {
  const [code, setCode] = useState('')
  return (
    <>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-success-tint text-success-fg">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-fg-primary">Verificación en dos pasos</h1>
          <p className="text-sm text-fg-secondary">{emailMasked}</p>
        </div>
      </div>

      <p className="mt-4 text-sm text-fg-secondary">
        Ingresa el código de 6 dígitos de tu app de autenticación. También puedes usar uno
        de tus códigos de respaldo (formato XXXX-XXXX).
      </p>

      {error && (
        <div className="mt-5 flex items-start gap-2 rounded-lg bg-error-tint px-3 py-2.5 text-sm text-error-fg">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={(e) => onSubmit(e, code)} className="mt-5 space-y-4">
        <div>
          <label
            htmlFor="totp"
            className="block text-xs font-medium uppercase tracking-wider text-fg-muted"
          >
            Código
          </label>
          <input
            id="totp"
            inputMode="text"
            autoComplete="one-time-code"
            autoFocus
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={12}
            className="mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-center font-mono text-base tracking-[0.3em] text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            placeholder="123456"
          />
        </div>
        <button
          type="submit"
          disabled={loading || code.length < 6}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Validando…
            </>
          ) : (
            <>Confirmar</>
          )}
        </button>
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          className="block w-full text-center text-xs text-fg-muted hover:text-fg-secondary"
        >
          ← Volver y usar otra cuenta
        </button>
        <Link
          to={ROUTES.MFA_RECOVERY_REQUEST}
          className="block text-center text-xs text-fg-muted hover:text-error-fg"
        >
          Perdí mi 2FA y no tengo códigos de respaldo
        </Link>
      </form>
    </>
  )
}

/**
 * Routing post-success de login (password o TOTP).
 *
 * Política: el step intermedio /select-org se muestra SIEMPRE que el
 * usuario pertenezca a ≥1 empresa. Esto cubre dos casos:
 *   - 1 empresa: el user confirma la entrada y tiene acceso visible a
 *     "Mi cuenta" (panel de identidad de Tax Account, separado del
 *     dashboard de operación).
 *   - 2+ empresas: además elige con cuál operar.
 *
 * Si el user es USER puro (0 empresas — persona natural sin org), va
 * directo al dashboard (no hay nada que elegir).
 *
 * Si /auth/me falla por alguna razón, default = dashboard.
 */
async function routePostLogin(navigate: ReturnType<typeof useNavigate>) {
  try {
    const me = await authApi.me()
    if ((me.memberships?.length ?? 0) >= 1) {
      markNeedsOrgSelection()
      navigate(ROUTES.SELECT_ORG, { replace: true })
      return
    }
  } catch {
    // Bootstrap falló — caemos al dashboard.
  }
  navigate(ROUTES.DASHBOARD, { replace: true })
}

function maskEmail(email: string): string {
  const [user, domain] = email.split('@')
  if (!domain || user.length <= 2) return email
  return `${user[0]}${'•'.repeat(Math.max(2, user.length - 2))}${user.at(-1)}@${domain}`
}

function extractError(err: unknown): string {
  // eslint-disable-next-line no-console
  console.error('[login] error:', err)
  const e = err as {
    code?: string
    message?: string
    response?: {
      status?: number
      data?: { detail?: { message?: string } | string; message?: string }
    }
  }
  if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message ?? '')) {
    return 'El servidor tardó demasiado. Inténtalo de nuevo.'
  }
  if (!e.response) {
    return 'No pudimos contactar al servidor. Revisa tu conexión.'
  }

  const status = e.response.status
  const detail = e.response.data?.detail

  if (detail && typeof detail === 'object' && detail.message) return detail.message
  if (typeof detail === 'string') return detail
  if (e.response.data?.message) return e.response.data.message

  if (status === 401) return 'Código incorrecto o expirado.'
  if (status === 403) return 'No puedes acceder. Verifica tu correo.'
  if (status === 423) return 'Cuenta bloqueada por intentos fallidos. Espera unos minutos.'
  if (status === 429) return 'Demasiados intentos. Espera un momento.'
  if (status && status >= 500) return 'El servicio está temporalmente caído.'

  return 'No pudimos iniciar sesión. Inténtalo de nuevo.'
}
